import { useCallback, useEffect, useRef, useState } from 'react';
import { useRepositories } from '../../../infrastructure/http/RepositoryProvider';
import type { Device } from '../../../domain/Device';
import type { Site } from '../../../domain/Site';
import { weatherLocationFor, type WeatherLocation } from '../../../domain/DeviceLocation';
import type { AgroForecast } from '../../../domain/Weather';
import type { StationRange, WeatherStationOverview } from '../../../domain/WeatherStation';

const POLL_INTERVAL_MS = 60 * 1000;
const FORECAST_TTL_MS = 30 * 60 * 1000;

// Usado por el home (sección de la estación) y por cada pantalla de detalle
// de métrica. Este hook puede montarse después de que la página ya entró en
// pantalla (el home lo muestra recién cuando sabe que hay una estación), por
// eso usa useEffect + intervalo propio en vez de los hooks de ciclo de vida de Ionic.
export function useWeatherStation(deviceId: string | undefined, initialRange: StationRange = '24h') {
  const { devices: deviceRepo, weatherStations, weather, sites: siteRepo } = useRepositories();
  const [device, setDevice] = useState<Device | null>(null);
  const [weatherLocation, setWeatherLocation] = useState<WeatherLocation | null>(null);
  const [overview, setOverview] = useState<WeatherStationOverview | null>(null);
  const [range, setRange] = useState<StationRange>(initialRange);
  const [loading, setLoading] = useState(true);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forecast, setForecast] = useState<AgroForecast | null>(null);
  const [forecastStatus, setForecastStatus] = useState<'loading' | 'ok' | 'unset' | 'error'>('loading');
  const requestIdRef = useRef(0);
  const forecastKeyRef = useRef<{ key: string; at: number } | null>(null);

  const loadForecast = useCallback(
    // El pronóstico es el del lugar de ESTA estación (GPS o marcada a mano, o
    // la de su campo) — nunca el del usuario: puede tener estaciones en
    // campos distintos.
    (location: WeatherLocation | null) => {
      if (!location) {
        setForecastStatus('unset');
        return;
      }
      const key = `${location.lat.toFixed(2)},${location.lon.toFixed(2)}`;
      const cached = forecastKeyRef.current;
      if (cached && cached.key === key && Date.now() - cached.at < FORECAST_TTL_MS) return;
      forecastKeyRef.current = { key, at: Date.now() };
      weather
        .getAgroForecast(location.lat, location.lon)
        .then((data) => {
          setForecast(data);
          setForecastStatus('ok');
        })
        .catch(() => {
          forecastKeyRef.current = null;
          setForecastStatus('error');
        });
    },
    [weather]
  );

  const load = useCallback(
    async (showSpinner: boolean) => {
      if (!deviceId) return;
      const requestId = ++requestIdRef.current;
      if (showSpinner) setRangeLoading(true);
      try {
        const [{ device: found }, { station }, sites] = await Promise.all([
          deviceRepo.get(deviceId),
          weatherStations.getOverview(deviceId, range),
          siteRepo
            .list()
            .then((r) => r.sites)
            .catch((): Site[] => []),
        ]);
        if (requestIdRef.current !== requestId) return;
        const location = weatherLocationFor(found, sites);
        setDevice(found);
        setWeatherLocation(location);
        setOverview(station);
        setError(null);
        loadForecast(location);
      } catch (err) {
        if (requestIdRef.current !== requestId) return;
        setError(err instanceof Error ? err.message : 'No se pudo cargar la estación.');
      } finally {
        if (requestIdRef.current === requestId) {
          setLoading(false);
          setRangeLoading(false);
        }
      }
    },
    [deviceId, range, deviceRepo, weatherStations, siteRepo, loadForecast]
  );

  useEffect(() => {
    load(true);
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') load(false);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [load]);

  const latestAt = overview
    ? Object.values(overview.current)
        .filter((r): r is NonNullable<typeof r> => r !== null)
        .map((r) => r.recordedAt)
        .sort()
        .pop() ?? null
    : null;

  return {
    device,
    overview,
    range,
    setRange,
    loading,
    rangeLoading,
    error,
    forecast,
    forecastStatus,
    weatherLocation,
    latestAt,
    reload: () => load(true),
  };
}
